import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiRequest } from '@/lib/supabase'

interface BioPage {
  id: string
  slug: string
  title: string
  bio: string | null
  profile_image_url: string | null
  theme: string
  is_published: boolean
  view_count: number
  created_at: string
}

export function BioPages() {
  const { data, isLoading } = useQuery<{ success: boolean; data: BioPage[] }>({
    queryKey: ['bio-pages'],
    queryFn: () => apiRequest('/api/bio-pages'),
  })

  const pages = data?.data || []

  return (
    <div>
      <div className="flex flex-wrap justify-between items-end gap-3 mb-8">
        <div>
          <h2 className="text-[#0d121b] dark:text-white text-3xl font-black tracking-tight">Bio Pages</h2>
          <p className="text-[#4c669a] dark:text-gray-400 text-sm mt-1">
            Create Linktree-style bio link pages
          </p>
        </div>
        <Link
          to="/bio-pages/new"
          className="px-4 py-2 bg-[#1754cf] text-white text-sm font-medium rounded-lg hover:opacity-90 flex items-center gap-2 shadow-[0_4px_14px_0_rgba(23,84,207,0.39)]"
        >
          <span className="material-symbols-outlined text-[18px]">add</span>
          Create Bio Page
        </Link>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white dark:bg-[#1c2230] rounded-xl border border-[#e7ebf3] dark:border-white/10 p-5 animate-pulse">
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-3" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            </div>
          ))}
        </div>
      ) : pages.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-[#1c2230] rounded-xl border border-[#e7ebf3] dark:border-white/10">
          <span className="material-symbols-outlined text-5xl text-[#4c669a] dark:text-gray-500 mb-4 block">web</span>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">No bio pages yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
            Create your first bio link page to share all your important links in one place.
          </p>
          <Link
            to="/bio-pages/new"
            className="inline-block px-5 py-2.5 bg-[#1754cf] text-white text-sm font-medium rounded-lg hover:opacity-90"
          >
            Create Your First Bio Page
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages.map(page => (
            <Link
              key={page.id}
              to={`/bio-pages/${page.id}`}
              className="bg-white dark:bg-[#1c2230] rounded-xl border border-[#e7ebf3] dark:border-white/10 p-5 hover:shadow-lg transition-shadow group"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white group-hover:text-[#1754cf] transition-colors">
                    {page.title}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">@{page.slug}</p>
                </div>
                <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                  page.is_published
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                }`}>
                  {page.is_published ? 'Published' : 'Draft'}
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-[14px]">visibility</span>
                  {page.view_count.toLocaleString()} views
                </span>
                <span>
                  {new Date(page.created_at).toLocaleDateString()}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
